import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import {
  fetchLedger, createLedgerEvent, updateLedgerEvent, deleteLedgerEvent,
  type LedgerEvent, type LedgerEventType, type LedgerEventWrite,
} from '../../lib/ledgerApi'
import { Panel, Spinner, ErrorState, EmptyState, Badge, Button, Table, Json, type Column } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'

const EVENT_TYPES: LedgerEventType[] = ['probe', 'decision', 'promotion', 'task_closed']

const TYPE_TONE: Record<LedgerEventType, 'blue' | 'indigo' | 'green' | 'neutral'> = {
  probe: 'blue',
  decision: 'indigo',
  promotion: 'green',
  task_closed: 'neutral',
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const shortId = (id?: string) => (id ? id.slice(0, 8) : null)

const linkCls = 'text-indigo-600 hover:underline'

function RunLink({ runId, short }: { runId: string; short?: boolean }) {
  return (
    <Link to="/agents/runs/$runId" params={{ runId }} className={`${linkCls} font-mono text-xs`}>
      {short ? shortId(runId) : runId}
    </Link>
  )
}

function ThesisLink({ thesisId, short }: { thesisId: string; short?: boolean }) {
  return (
    <Link to="/theses/$thesisId" params={{ thesisId }} className={`${linkCls} font-mono text-xs`}>
      {short ? shortId(thesisId) : thesisId}
    </Link>
  )
}

export function LedgerTab({ active }: { active: boolean }) {
  const qc = useQueryClient()
  const [typeFilter, setTypeFilter] = useState<string>('')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ledger', typeFilter],
    queryFn: () => fetchLedger(typeFilter ? { event_type: typeFilter } : undefined),
    enabled: active,
  })
  const [editing, setEditing] = useState<LedgerEvent | 'new' | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  if (isLoading) return <Spinner />
  if (isError || !data) return <ErrorState message="Couldn't load the ledger." />

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ledger'] })

  const onDelete = async (ev: LedgerEvent) => {
    if (!confirm(`Delete ledger event — ${ev.event_type}: ${ev.summary.slice(0, 80)}?`)) return
    setBusyId(ev.id)
    try {
      await deleteLedgerEvent(ev.id)
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyId(null)
    }
  }

  const mono = (v?: string) => (v ? <span className="text-xs text-gray-500 font-mono">{shortId(v)}</span> : <span className="text-gray-300">—</span>)

  const columns: Column<LedgerEvent>[] = [
    { key: 'id', header: 'ID', render: (ev) => mono(ev.id) },
    { key: 'time', header: 'When', render: (ev) => <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(ev.created_at)}</span> },
    { key: 'type', header: 'Type', render: (ev) => <Badge tone={TYPE_TONE[ev.event_type] ?? 'neutral'}>{ev.event_type}</Badge> },
    { key: 'summary', header: 'Summary', render: (ev) => (
      <button
        type="button"
        onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
        className="text-left text-gray-800 hover:text-indigo-700 max-w-md block whitespace-pre-wrap"
        title={expandedId === ev.id ? 'Hide details' : 'Show details'}
      >
        {ev.summary}
      </button>
    ) },
    { key: 'tags', header: 'Tags', render: (ev) => (
      <div className="flex flex-wrap gap-1">
        {ev.tags.map((t) => <Badge key={t}>{t}</Badge>)}
      </div>
    ) },
    { key: 'idea', header: 'Idea', render: (ev) => mono(ev.idea_id) },
    { key: 'parent_idea', header: 'Parent idea', render: (ev) => mono(ev.parent_idea_id) },
    { key: 'thesis', header: 'Thesis', render: (ev) => (ev.thesis_id ? <ThesisLink thesisId={ev.thesis_id} short /> : <span className="text-gray-300">—</span>) },
    { key: 'related_thesis', header: 'Related thesis', render: (ev) => (
      ev.related_thesis_id ? (
        <span className="text-xs">
          <ThesisLink thesisId={ev.related_thesis_id} short />
          {ev.relation_type && <span className="text-gray-500"> ({ev.relation_type})</span>}
        </span>
      ) : <span className="text-gray-300">—</span>
    ) },
    { key: 'family', header: 'Family', render: (ev) => mono(ev.family_id) },
    { key: 'task', header: 'Task', render: (ev) => mono(ev.task_id) },
    { key: 'run', header: 'Run', render: (ev) => (ev.run_id ? <RunLink runId={ev.run_id} short /> : <span className="text-gray-300">—</span>) },
    { key: 'actions', header: '', render: (ev) => (
      <div className="flex gap-2 justify-end">
        {ev.can_edit && (
          <>
            <Button variant="secondary" onClick={() => setEditing(ev)}>Edit</Button>
            <Button variant="secondary" disabled={busyId === ev.id} onClick={() => onDelete(ev)} className="!text-red-600">Delete</Button>
          </>
        )}
      </div>
    ) },
  ]

  const expanded = expandedId ? data.find((ev) => ev.id === expandedId) : null

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-gray-700">
        The research ledger is the durable record agents write as they work: cheap probes, decisions,
        promotions into the thesis pipeline, and closed tasks.
      </div>
      <div className="flex items-center justify-between">
        <label className="text-xs text-gray-700 flex items-center gap-2">
          Event type
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
            <option value="">All</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <Button variant="primary" onClick={() => setEditing('new')}>Create ledger event</Button>
      </div>
      <Panel title={`Ledger events — ${data.length}`}>
        {data.length === 0 ? (
          <EmptyState title="No ledger events yet." hint="Agents record research probes and decisions here." />
        ) : (
          <Table columns={columns} rows={data} empty="No ledger events." />
        )}
      </Panel>
      {expanded && (
        <Panel title={`Event ${shortId(expanded.id)} — ${expanded.event_type}`}>
          <div className="flex flex-col gap-2 text-sm text-gray-800">
            <p className="whitespace-pre-wrap">{expanded.summary}</p>
            {expanded.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {expanded.tags.map((t) => <Badge key={t}>{t}</Badge>)}
              </div>
            )}
            <div className="text-xs text-gray-500 font-mono flex flex-col gap-0.5">
              <span>id: {expanded.id}</span>
              <span>org: {expanded.org_id}</span>
              {expanded.user_id && <span>user: {expanded.user_id}</span>}
              <span>created: {fmtDateTime(expanded.created_at)}</span>
              {expanded.idea_id && <span>idea: {expanded.idea_id}</span>}
              {expanded.parent_idea_id && <span>parent idea: {expanded.parent_idea_id}</span>}
              {expanded.thesis_id && <span>thesis: <ThesisLink thesisId={expanded.thesis_id} /></span>}
              {expanded.related_thesis_id && (
                <span>related thesis: <ThesisLink thesisId={expanded.related_thesis_id} /> ({expanded.relation_type ?? 'related'})</span>
              )}
              {expanded.family_id && <span>family: {expanded.family_id}</span>}
              {expanded.task_id && <span>task: {expanded.task_id}</span>}
              {expanded.run_id && <span>run: <RunLink runId={expanded.run_id} /></span>}
            </div>
            <Json value={expanded.payload} />
          </div>
        </Panel>
      )}
      {editing && (
        <LedgerModal
          event={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate() }}
        />
      )}
    </div>
  )
}

function LedgerModal({ event, onClose, onSaved }: { event: LedgerEvent | null; onClose: () => void; onSaved: () => void }) {
  const [eventType, setEventType] = useState<LedgerEventType>(event?.event_type ?? 'probe')
  const [summary, setSummary] = useState(event?.summary ?? '')
  const [tagsText, setTagsText] = useState((event?.tags ?? []).join(', '))
  const [payloadText, setPayloadText] = useState(event ? JSON.stringify(event.payload, null, 2) : '{}')
  const [thesisId, setThesisId] = useState(event?.thesis_id ?? '')
  const [familyId, setFamilyId] = useState(event?.family_id ?? '')
  const [taskId, setTaskId] = useState(event?.task_id ?? '')
  const [ideaId, setIdeaId] = useState(event?.idea_id ?? '')
  const [saving, setSaving] = useState(false)

  const build = (): LedgerEventWrite | string => {
    if (!summary.trim()) return 'Add a summary.'
    let payload: Record<string, unknown> | undefined
    const raw = payloadText.trim()
    if (raw && raw !== '{}') {
      try {
        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return 'Payload must be a JSON object.'
        payload = parsed as Record<string, unknown>
      } catch {
        return 'Payload must be valid JSON.'
      }
    }
    const links: Partial<LedgerEventWrite> = {}
    for (const [label, value, key] of [
      ['Thesis', thesisId, 'thesis_id'],
      ['Family', familyId, 'family_id'],
      ['Task', taskId, 'task_id'],
      ['Idea', ideaId, 'idea_id'],
    ] as const) {
      const v = value.trim()
      if (!v) continue
      if (!UUID_RE.test(v)) return `${label} id must be a UUID.`
      links[key] = v
    }
    return {
      event_type: eventType,
      summary: summary.trim(),
      tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
      payload,
      ...links,
    }
  }

  const onSave = async () => {
    const body = build()
    if (typeof body === 'string') {
      alert(body)
      return
    }
    setSaving(true)
    try {
      if (event) await updateLedgerEvent(event.id, body)
      else await createLedgerEvent(body)
      onSaved()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'px-2 py-1.5 text-xs border border-gray-300 rounded-md'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-xl w-[min(560px,90vw)] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{event ? 'Edit ledger event' : 'Create ledger event'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Event type
            <select value={eventType} onChange={(e) => setEventType(e.target.value as LedgerEventType)} className={inputCls}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Summary
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className={inputCls} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Tags (comma-separated)
            <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="rdn, baseline" className={inputCls} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Payload (JSON object)
            <textarea value={payloadText} onChange={(e) => setPayloadText(e.target.value)} rows={5} className={`${inputCls} font-mono`} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Thesis id (optional)
            <input value={thesisId} onChange={(e) => setThesisId(e.target.value)} className={`${inputCls} font-mono`} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Family id (optional)
            <input value={familyId} onChange={(e) => setFamilyId(e.target.value)} className={`${inputCls} font-mono`} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Task id (optional)
            <input value={taskId} onChange={(e) => setTaskId(e.target.value)} className={`${inputCls} font-mono`} />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Idea id (optional)
            <input value={ideaId} onChange={(e) => setIdeaId(e.target.value)} className={`${inputCls} font-mono`} />
          </label>
          {event?.run_id && (
            <p className="text-xs text-gray-500 font-mono">run: {event.run_id} (set by the agent, not editable)</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  )
}
